import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";

/**
 * Sincronización de seguridad de administrador (PIN admin)
 * Documento: workspaces/{workspaceId}/settings/security
 *
 * Contrato de onSecurityChange(security, error):
 * - snapshot exists()===true  -> security = { adminPinHash, adminPinSalt, adminPinIterations, adminPinVersion, adminPinUpdatedAt }
 * - snapshot exists()===false -> security = { adminPinHash: null, adminPinSalt: null, adminPinIterations: null, adminPinVersion: null, adminPinUpdatedAt: null }
 * - error de Firestore        -> security = null, error = objeto de error
 *
 * El consumidor debe distinguir "snapshot real sin PIN" (confirmado, no hay PIN)
 * de "error" (no confirmado, no se debe interpretar como "sin PIN" para evitar bypass).
 */
export function subscribeToSecurity(workspaceId, db, onSecurityChange) {
  if (!workspaceId || !db) {
    console.error("[SECURITY_SYNC] Invalid workspaceId or db");
    return () => {};
  }

  const safeOnSecurityChange = typeof onSecurityChange === "function"
    ? onSecurityChange
    : () => {
        console.warn("[SECURITY_SYNC] onSecurityChange is not a function, using no-op");
      };

  try {
    const securityRef = doc(db, "workspaces", workspaceId, "settings", "security");
    console.log("[SECURITY_SYNC] Subscribing to:", securityRef.path);

    const unsubscribe = onSnapshot(
      securityRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() || {};
          const normalized = {
            adminPinHash: data.adminPinHash || null,
            adminPinSalt: data.adminPinSalt || null,
            adminPinIterations: data.adminPinIterations || null,
            adminPinVersion: data.adminPinVersion || null,
            adminPinUpdatedAt: data.adminPinUpdatedAt || null,
          };
          console.log("[SECURITY_SYNC] 📡 Security updated from Firestore (PIN present:", !!normalized.adminPinHash, ")");
          safeOnSecurityChange(normalized, null);
        } else {
          // Documento no existe: estado real confirmado, sin PIN admin configurado.
          console.log("[SECURITY_SYNC] Security doc does not exist, no admin PIN configured");
          safeOnSecurityChange({
            adminPinHash: null,
            adminPinSalt: null,
            adminPinIterations: null,
            adminPinVersion: null,
            adminPinUpdatedAt: null,
          }, null);
        }
      },
      (error) => {
        console.error("[SECURITY_SYNC] ❌ Subscription error:", error);
        safeOnSecurityChange(null, error);
      }
    );

    console.log("[SECURITY_SYNC] ✅ Subscription active");
    return unsubscribe;
  } catch (error) {
    console.error("[SECURITY_SYNC] ❌ Error subscribing to security:", error);
    return () => {};
  }
}

/**
 * Actualiza únicamente los campos de seguridad de administrador en Firestore.
 * Usa merge:true para no afectar otros campos del documento (si los hubiera).
 * No guarda el PIN en texto plano: solo hash/salt/iterations/version.
 */
export async function updateAdminSecurity(workspaceId, db, updates, userId) {
  if (!workspaceId || !db || !updates || typeof updates !== 'object') {
    console.error("[SECURITY_SYNC] Invalid parameters for update");
    return false;
  }

  try {
    const securityRef = doc(db, "workspaces", workspaceId, "settings", "security");
    console.log("[SECURITY_SYNC] Updating security at:", securityRef.path);

    const dataToUpdate = {
      ...updates,
      updatedAt: serverTimestamp(),
      updatedBy: userId || 'unknown',
    };

    await setDoc(securityRef, dataToUpdate, { merge: true });
    console.log("[SECURITY_SYNC] ✅ Security updated in Firestore");
    return true;
  } catch (error) {
    console.error("[SECURITY_SYNC] ❌ Error updating security:", error);
    console.error("[SECURITY_SYNC] Error details:", error.message);
    return false;
  }
}
